// Página de una agencia en el backoffice, como la ve su dueño en la consola
// de la app (Agencia › Resumen): la cabecera de su ficha del Explorador, el
// plan que tiene de verdad (con condiciones a medida, prueba y margen), lo
// que usa de él, lo que paga, lo que incluye, cuánto lleva configurado, quién
// la lleva y, debajo, sus creadores, misiones, eventos, historial y la ficha
// pública entera. Las acciones son las del Admin de la app: editar la ficha,
// las condiciones del plan, el dueño, las insignias y el pack.
//
// El plan y la ficha salen del código de la app (lib/agencyApp.js); las
// cuentas y lo que se guarda, de lib/agencyAdmin.js (con tests).

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Pagination,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure
} from '@nextui-org/react';
import {
  FieldPath,
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { loadDashboard, useDashboardStore } from '../lib/dashboardStore';
import { maskEmail } from '../lib/mask';
import { AgencyShared as S } from '../lib/agencyApp';
import {
  INCLUDED_FEATURES,
  nextPlanGains,
  planAlertQuota,
  planUsage,
  rosterMembers,
  setupSteps,
  shortLabel,
  staffList,
  toMs
} from '../lib/agencyAdmin';
import { confirmAndDeleteAgency, setAgencyValidated } from '../lib/agencyActions';
import DashboardSection from '../components/DashboardSection';
import { AgencyStateChip, TierChip, formatMoney, formatMoneyMap } from '../components/AgencyBillingSection';
import { AgencyHero, AgencySheet, useAgencyProfile, visibilityNote } from '../components/agency/AppAgencyView';
import AgencyPlanEditor from '../components/agency/AgencyPlanEditor';
import AgencyProfileEditor from '../components/agency/AgencyProfileEditor';
import AgencyOwnerEditor from '../components/agency/AgencyOwnerEditor';

const LIST_LIMIT = 2000;
const MEMBERS_PAGE = 25;
const PACK_LABELS = { none: 'Sin pack', basic: 'Básico', standard: 'Standard', advanced: 'Avanzado' };
const HIGHLIGHT_LABELS = { none: 'Sin destacar', basic: 'Básico', mid: 'Medio', top: 'Superior' };
const PLAN_CHIP = { rookie: 'default', silver: 'default', gold: 'warning', titanium: 'secondary' };
const TICKET_MODES = { auto: 'Automático', approval: 'Con revisión', hybrid: 'Híbrido' };
const MISSION_STATUS = {
  active: ['En curso', 'success'],
  scheduled: ['Programada', 'primary'],
  paused: ['Pausada', 'warning'],
  completed: ['Terminada', 'default'],
  draft: ['Borrador', 'default'],
  archived: ['Archivada', 'default']
};
const MEMBER_STATUS = { approved: 'Aprobado', linked: 'Vinculado', pending: 'Pendiente', rejected: 'Rechazado', left: 'Se fue', removed: 'Quitado' };

function fmtDate(ms) {
  return ms ? new Date(ms).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}
function fmtDateTime(ms) {
  return ms ? new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}
const limitText = (n) => (S.isUnlimited(n) ? 'Ilimitado' : String(Number(n)));

// Escucha una colección de la agencia (where agencyId == id).
function useAgencyCollection(name, agencyId, { live = true, max = LIST_LIMIT } = {}) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!agencyId) return undefined;
    setRows(null);
    const q = query(collection(db, name), where('agencyId', '==', agencyId), fsLimit(max));
    if (!live) {
      let alive = true;
      getDocs(q)
        .then((snap) => { if (alive) setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); })
        .catch((e) => { console.warn(`[AgenciaDetail] ${name}:`, e.message); if (alive) setRows([]); });
      return () => { alive = false; };
    }
    return onSnapshot(
      q,
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => { console.warn(`[AgenciaDetail] ${name}:`, e.message); setRows([]); }
    );
  }, [name, agencyId, live, max]);
  return rows;
}

// ¿Hay al menos uno? (Biblioteca: comunicados, formaciones y recursos.)
function useAgencyHasAny(names, agencyId) {
  const [has, setHas] = useState(null);
  useEffect(() => {
    if (!agencyId) return undefined;
    let alive = true;
    setHas(null);
    Promise.all(names.map((name) => getDocs(query(collection(db, name), where('agencyId', '==', agencyId), fsLimit(1)))
      .then((snap) => !snap.empty)
      .catch(() => false)))
      .then((list) => { if (alive) setHas(list.some(Boolean)); });
    return () => { alive = false; };
  }, [names.join('|'), agencyId]); // eslint-disable-line react-hooks/exhaustive-deps
  return has;
}

// Dueños: los del doc (ownerId/ownerUid) y los que tienen agencyRoles[id] = 'owner'.
function useOwners(agency, refresh) {
  const [owners, setOwners] = useState(null);
  const id = agency?.id;
  const docOwners = [agency?.ownerId, agency?.ownerUid].filter((x) => typeof x === 'string' && x);
  const key = `${id}|${docOwners.join(',')}|${refresh}`;
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    (async () => {
      const map = new Map();
      try {
        const snap = await getDocs(query(collection(db, 'users'), where(new FieldPath('agencyRoles', id), '==', 'owner'), fsLimit(20)));
        snap.docs.forEach((d) => map.set(d.id, { uid: d.id, ...d.data() }));
      } catch (e) {
        console.warn('[AgenciaDetail] dueños por rol:', e.message);
      }
      for (const uid of docOwners) {
        if (map.has(uid)) continue;
        try {
          const d = await getDoc(doc(db, 'users', uid));
          map.set(uid, d.exists() ? { uid, ...d.data() } : { uid, missing: true });
        } catch (_) {
          map.set(uid, { uid, missing: true });
        }
      }
      const list = [...map.values()].map((u) => ({
        uid: u.uid,
        email: u.email || (u.uid === agency.ownerId ? agency.ownerEmail : '') || '',
        tiktokUsername: u.tiktokUsername || '',
        displayName: u.displayName || '',
        role: u.role || '',
        missing: u.missing === true,
        hasRole: !!(u.agencyRoles && u.agencyRoles[id] === 'owner')
      }));
      list.sort((a, b) => Number(b.uid === agency.ownerId) - Number(a.uid === agency.ownerId));
      if (alive) setOwners(list);
    })();
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return owners;
}

// Foto de un creador: las URL de TikTok caducan y se veían rotas. Si no
// carga, su inicial.
function MemberAvatar({ url, name }) {
  const [broken, setBroken] = useState(false);
  const initial = String(name || '?').replace(/^[^a-z0-9]+/i, '')[0]?.toUpperCase() || '?';
  if (!url || broken) {
    return <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-default-200 text-xs">{initial}</span>;
  }
  return <img src={url} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} className="h-7 w-7 shrink-0 rounded-full object-cover" />;
}

// «2000+» cuando la consulta llega a su tope.
const countLabel = (list) => (list ? ` (${list.length}${list.length >= LIST_LIMIT ? '+' : ''})` : '');

function UsageBar({ label, used, max, hint }) {
  const unlimited = S.isUnlimited(max);
  const pct = unlimited || !Number(max) ? 0 : Math.min(100, Math.round((Number(used) / Number(max)) * 100));
  const over = !unlimited && Number(used) > Number(max);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span>{label}</span>
        <span className={`tabular-nums ${over ? 'text-danger-500' : 'text-default-600'}`}>
          {used == null ? '…' : used} / {limitText(max)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-default-200">
        <div className={`h-full rounded-full ${over ? 'bg-danger-500' : 'bg-primary'}`} style={{ width: `${unlimited ? 0 : over ? 100 : pct}%` }} />
      </div>
      {hint && <span className="text-xs text-default-500">{hint}</span>}
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-default-100 py-1.5 text-sm last:border-b-0">
      <span className="text-default-500">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

function PlanChips({ agency, effective }) {
  return (
    <>
      <Chip size="sm" variant="flat" color={PLAN_CHIP[effective.id] || 'default'}>{effective.label}</Chip>
      {effective.trial && <Chip size="sm" variant="flat" color="primary">Prueba · {effective.trial.daysLeft} días</Chip>}
      {effective.grace && <Chip size="sm" variant="flat" color="warning">Margen de ajuste</Chip>}
      {agency.validated ? (
        <Chip size="sm" variant="flat" color="success">Validada</Chip>
      ) : (
        <Chip size="sm" variant="flat" color="warning">Pendiente de validar</Chip>
      )}
      {agency.published === false ? (
        <Chip size="sm" variant="flat">Sin publicar</Chip>
      ) : (
        <Chip size="sm" variant="flat" color="success">Publicada</Chip>
      )}
      {agency.testData === true && <Chip size="sm" variant="flat" color="secondary">TEST</Chip>}
    </>
  );
}

export default function AgenciaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite, isViewer } = useAuth();
  const [agency, setAgency] = useState(undefined); // undefined cargando · null no existe
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null);
  const [tab, setTab] = useState('creators');
  const [memberFilter, setMemberFilter] = useState('roster');
  const [memberSearch, setMemberSearch] = useState('');
  const [membersOpen, setMembersOpen] = useState(false);
  const [memberPage, setMemberPage] = useState(1);
  const [ownerRefresh, setOwnerRefresh] = useState(0);
  const planEditor = useDisclosure();
  const profileEditor = useDisclosure();
  const ownerEditor = useDisclosure();
  const jsonView = useDisclosure();

  useEffect(() => {
    if (!id) return undefined;
    setAgency(undefined);
    setLoadError(null);
    return onSnapshot(
      doc(db, 'agencies', id),
      (snap) => setAgency(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      (e) => { setLoadError(e.message || 'No se ha podido leer la agencia.'); setAgency(null); }
    );
  }, [id]);

  const members = useAgencyCollection('agency_members', id);
  const activity = useAgencyCollection('agency_activity', id, { max: 500 });
  const challenges = useAgencyCollection('agency_challenges', id, { live: false });
  const events = useAgencyCollection('agency_events', id, { live: false });
  const profiles = useAgencyCollection('agency_profiles', id, { live: false, max: 500 });
  const hasLibrary = useAgencyHasAny(['agency_news', 'agency_training', 'agency_library_assets'], id);
  const owners = useOwners(agency, ownerRefresh);

  const { data: dashboard, error: dashboardError } = useDashboardStore();
  useEffect(() => { loadDashboard().catch(() => {}); }, []);

  const roster = useMemo(() => (members ? rosterMembers(members) : null), [members]);
  const creatorsCount = roster ? roster.length : undefined;
  const prof = useAgencyProfile(agency || null, creatorsCount);
  const effective = useMemo(() => (agency ? S.agencyPlan(agency) : null), [agency]);
  const usage = useMemo(() => {
    if (!agency) return null;
    return planUsage(S, { agency, members: members || [], profiles: profiles?.length || 0, challenges: challenges || [], events: events || [] });
  }, [agency, members, profiles, challenges, events]);
  const steps = useMemo(() => {
    if (!agency) return [];
    const ownMissions = challenges ? challenges.filter((c) => c.draft !== true && c.archived !== true).length : null;
    return setupSteps(S, agency, {
      members: roster ? roster.length : null,
      licensed: roster ? roster.filter((m) => m.licenseType && m.licenseType !== 'none').length : null,
      staff: usage ? usage.staffCount : null,
      profiles: profiles ? profiles.length : null,
      challenges: ownMissions,
      events: events ? events.length : null,
      library: hasLibrary == null ? null : hasLibrary ? 1 : 0
    });
  }, [agency, roster, usage, profiles, challenges, events, hasLibrary]);
  const staff = useMemo(() => (agency ? staffList(agency) : []), [agency]);
  const billing = useMemo(() => {
    const subs = dashboard?.agencies?.subscriptions;
    if (!Array.isArray(subs) || !id) return null;
    return subs.filter((s) => s.agencyId === id);
  }, [dashboard, id]);

  const sortedActivity = useMemo(() => (activity || [])
    .map((a) => ({ ...a, atMs: toMs(a.at) }))
    .sort((a, b) => b.atMs - a.atMs), [activity]);

  const shownMembers = useMemo(() => {
    const base = memberFilter === 'roster' ? roster || [] : members || [];
    const needle = memberSearch.trim().toLowerCase().replace(/^@/, '');
    return base
      .filter((m) => !needle || String(m.tiktokUsername || '').toLowerCase().includes(needle) || String(m.backstageAgent || '').toLowerCase().includes(needle))
      .sort((a, b) => {
        const la = a.licenseType && a.licenseType !== 'none' ? 0 : 1;
        const lb = b.licenseType && b.licenseType !== 'none' ? 0 : 1;
        return la - lb || String(a.tiktokUsername || '').localeCompare(String(b.tiktokUsername || ''));
      });
  }, [members, roster, memberFilter, memberSearch]);

  const rosterStats = useMemo(() => {
    const list = roster || [];
    return {
      licensed: list.filter((m) => m.licenseType && m.licenseType !== 'none').length,
      live: list.filter((m) => m.backstageIsLive).length,
      withApp: list.filter((m) => m.userId).length
    };
  }, [roster]);
  const memberPages = Math.max(1, Math.ceil(shownMembers.length / MEMBERS_PAGE));
  const page = Math.min(memberPage, memberPages);
  const pageMembers = shownMembers.slice((page - 1) * MEMBERS_PAGE, page * MEMBERS_PAGE);

  const notify = (msg, type = 'ok') => setToast({ type, msg });

  async function toggleValidated() {
    try {
      const next = !agency.validated;
      const welcomed = await setAgencyValidated(agency, next);
      notify(next ? `Agencia validada.${welcomed}` : 'Agencia invalidada.');
    } catch (e) {
      notify(e.message, 'error');
    }
  }

  async function remove() {
    try {
      const res = await confirmAndDeleteAgency(agency);
      if (!res) return;
      if (res.deleted) navigate('/agencias/consulta', { replace: true });
      else notify(res.msg, 'error');
    } catch (e) {
      notify(e.message, 'error');
    }
  }

  if (agency === undefined) {
    return <div className="flex justify-center p-10"><Spinner /></div>;
  }
  if (!agency) {
    return (
      <div className="flex flex-col gap-3">
        <Link to="/agencias/consulta" className="text-sm text-primary">← Agencias</Link>
        <p className="text-default-500">{loadError || `No existe ninguna agencia con el ID ${id}.`}</p>
      </div>
    );
  }

  const note = visibilityNote(prof);
  const next = effective ? nextPlanGains(S, effective) : null;
  const known = steps.filter((s) => s.done !== null);
  const doneSteps = known.filter((s) => s.done).length;
  const alertQuota = (() => {
    const v = agency.planOverrides?.limits?.maxAlerts;
    return v != null && v !== '' && Number.isFinite(Number(v)) ? Number(v) : planAlertQuota(effective.id);
  })();
  const ownerEmail = (email) => (isViewer ? maskEmail(email) : email);
  const ticketsMode = agency.ticketsMode ? TICKET_MODES[agency.ticketsMode] || agency.ticketsMode : 'Sin elegir';
  const packStatus = agency.animPackStatus || (agency.animPackDeliveredAt ? 'delivered' : (agency.animPackRequestedAt ? 'requested' : ''));

  return (
    <div className="flex flex-col gap-5">
      <Link to="/agencias/consulta" className="text-sm text-primary">← Agencias</Link>

      {/* ── Cabecera ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">{agency.name || agency.id}</h1>
            <PlanChips agency={agency} effective={effective} />
          </div>
          <p className="mt-1 text-xs text-default-500">
            {agency.id}
            {agency.region ? ` · ${agency.region}` : ''}
            {` · creada el ${fmtDate(toMs(agency.createdAt))}`}
            {agency.validated && toMs(agency.validatedAt) ? ` · validada el ${fmtDate(toMs(agency.validatedAt))}` : ''}
            {agency.published !== false && toMs(agency.publishedAt) ? ` · publicada el ${fmtDate(toMs(agency.publishedAt))}` : ''}
          </p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="flat" onPress={profileEditor.onOpen}>Editar ficha</Button>
            <Button size="sm" color="primary" onPress={planEditor.onOpen}>Condiciones del plan</Button>
            <Button size="sm" variant="flat" onPress={ownerEditor.onOpen}>Dueño, insignias y pack</Button>
            <Button size="sm" variant="flat" color={agency.validated ? 'warning' : 'success'} onPress={toggleValidated}>
              {agency.validated ? 'Invalidar' : 'Validar'}
            </Button>
            <Dropdown>
              <DropdownTrigger>
                <Button size="sm" variant="light" isIconOnly aria-label="Más acciones">⋯</Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Más acciones"
                onAction={(key) => {
                  if (key === 'json') jsonView.onOpen();
                  if (key === 'delete') remove();
                }}
              >
                <DropdownItem key="json">Ver el documento (JSON)</DropdownItem>
                <DropdownItem key="delete" className="text-danger" color="danger">Eliminar agencia</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>

      {toast && (
        <div className={`flex items-center justify-between gap-3 rounded-md border p-3 text-sm ${toast.type === 'ok' ? 'border-success-200 bg-success-50 text-success-700' : 'border-danger-200 bg-danger-50 text-danger-700'}`}>
          <span>{toast.msg}</span>
          <button className="text-xs underline" onClick={() => setToast(null)}>cerrar</button>
        </div>
      )}

      {/* ── La tarjeta de presentación, como en la app ── */}
      <div className="flex flex-col gap-2">
        <AgencyHero agency={agency} creators={creatorsCount} />
        {note && <p className="text-xs text-warning-600">{note}</p>}
      </div>

      {/* ── Plan y uso · Pago ── */}
      <div className="grid gap-x-10 gap-y-6 xl:grid-cols-2">
        <DashboardSection
          title="Plan y uso"
          subtitle={effective.hasOverrides ? `Plan ${effective.baseLabel} con condiciones a medida.` : `Plan ${effective.baseLabel} del catálogo.`}
          accent="#3b82f6"
        >
          <div className="flex flex-col gap-4">
            {effective.trial && (
              <p className="text-sm text-primary">
                Prueba de {effective.baseLabel} hasta el {fmtDateTime(effective.trial.endsAt)} ({S.trialLeftLabel(effective.trial.daysLeft).toLowerCase()}).
                Después vuelve a {S.PLANS[agency.plan]?.label || agency.plan}.{effective.trial.grantedBy ? ` La dio ${ownerEmail(effective.trial.grantedBy)}.` : ''}
              </p>
            )}
            {effective.grace && (
              <p className="text-sm text-warning-600">
                Margen de ajuste: conserva lo de {S.PLANS[effective.grace.from]?.label} hasta el {fmtDateTime(effective.grace.until)}; después pasa a {effective.graceTarget?.label || agency.plan}.
              </p>
            )}
            <UsageBar label="Créditos asignados" used={members ? usage.creditsUsed : null} max={effective.maxCredits} hint="Licencias Premium (2) y Performance (1) puestas a sus creadores." />
            <UsageBar label="Perfiles LIVE" used={profiles ? usage.profilesCount : null} max={effective.maxProfiles} />
            <UsageBar label="Managers" used={usage.staffCount} max={effective.maxManagers} hint="Equipo activo de Licencias, sin contar al dueño." />
            <div>
              <Fact label="Misiones en curso">{challenges ? usage.activeMissions : '…'}</Fact>
              <Fact label="Eventos de agencia activos">{events ? usage.activeAgencyEvents : '…'}</Fact>
              <Fact label="Tickets automáticos / mes">{limitText(effective.maxAutoTickets)} · modo {ticketsMode.toLowerCase()}</Fact>
              <Fact label="Directos vigilados a la vez">{alertQuota}</Fact>
              <Fact label="Pack de animaciones">
                {PACK_LABELS[effective.animPack] || effective.animPack}
                {packStatus === 'requested' && <span className="text-warning-600"> · lo ha pedido</span>}
                {packStatus === 'delivered' && <span className="text-success-500"> · entregado</span>}
              </Fact>
              <Fact label="Destacado en el buscador">{HIGHLIGHT_LABELS[effective.highlightLevel] || effective.highlightLevel}</Fact>
            </div>
            {next && (
              <p className="text-xs text-default-500">
                Siguiente plan: <span className="text-default-700">{next.label}</span>
                {next.gains.length ? ` · añade ${next.gains.join(', ')}` : ' · más créditos, perfiles y managers'}.
              </p>
            )}
          </div>
        </DashboardSection>

        <DashboardSection title="Pago del plan" subtitle="Suscripciones de Stripe enlazadas a esta agencia (Dashboard)." accent="#22c55e">
          {billing == null ? (
            <p className="text-sm text-default-500">{dashboardError ? `No se ha podido leer el Dashboard: ${dashboardError}` : 'Cargando cobros…'}</p>
          ) : billing.length === 0 ? (
            <p className="text-sm text-default-500">
              No hay ninguna suscripción de agencia enlazada. Se enlazan por el email del cliente de Stripe (o de su ficha del CRM)
              con el del dueño o el de contacto; si no, por el nombre exacto de la agencia.
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              {billing.map((row) => (
                <div key={row.id} className="flex flex-col">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <TierChip tier={row.tier} />
                    <AgencyStateChip state={row.state} />
                    {row.agencyPlan && row.agencyPlan !== row.tier && (
                      <span className="text-xs text-warning-600">En la app tiene {S.PLANS[row.agencyPlan]?.label || row.agencyPlan}</span>
                    )}
                  </div>
                  <Fact label="Cuota">{row.monthly ? `${formatMoney(row.monthly, row.currency, 2)} / mes` : '—'}</Fact>
                  <Fact label="Cobrado">{row.paidCount ? `${formatMoneyMap(row.paidTotal, 2)} en ${row.paidCount} ${row.paidCount === 1 ? 'cobro' : 'cobros'} (${formatMoneyMap(row.paidNet, 2)} sin impuestos)` : 'Nada todavía'}</Fact>
                  <Fact label="Último cobro">{row.lastPaidAt ? `${formatMoney(row.lastPaidAmount, row.lastPaidCurrency || row.currency, 2)} el ${fmtDate(row.lastPaidAt)}` : '—'}</Fact>
                  <Fact label="Periodo actual hasta">{fmtDate(row.currentPeriodEnd)}</Fact>
                  <Fact label="Alta">{fmtDate(row.createdAt)}</Fact>
                  {canWrite && (
                    <a
                      className="mt-1 text-xs text-primary hover:underline"
                      href={`https://dashboard.stripe.com/subscriptions/${encodeURIComponent(row.id)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir en Stripe
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </DashboardSection>
      </div>

      {/* ── Incluido · Configuración ── */}
      <div className="grid gap-x-10 gap-y-6 xl:grid-cols-2">
        <DashboardSection title="Incluido en el plan" subtitle="Lo que tiene ahora (con la prueba y las condiciones a medida)." accent="#a855f7">
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {INCLUDED_FEATURES.map(([key, k, f]) => {
              const on = !!effective.features[key];
              const base = !!(S.PLANS[effective.id]?.features || {})[key];
              const custom = typeof agency.planOverrides?.features?.[key] === 'boolean' && on !== base;
              return (
                <div key={key} className={`flex items-center gap-2 text-sm ${on ? '' : 'text-default-400'}`}>
                  <span className={on ? 'text-success-500' : ''}>{on ? '✓' : '✕'}</span>
                  <span>{shortLabel(S.t(k, f))}</span>
                  {custom && <Chip size="sm" variant="flat" color="secondary" className="h-4 text-[10px]">a medida</Chip>}
                </div>
              );
            })}
          </div>
        </DashboardSection>

        <DashboardSection title="Configuración de la agencia" subtitle={`${doneSteps} de ${steps.length} pasos hechos (los mismos de su consola).`} accent="#f59e0b">
          <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {steps.map((s) => (
              <div key={s.id} className="flex items-start gap-2 text-sm">
                <span className={s.done ? 'text-success-500' : s.done === false ? 'text-default-400' : 'text-default-300'}>
                  {s.done ? '✓' : s.done === false ? '○' : '…'}
                </span>
                <span className="min-w-0">
                  <span className={s.done ? '' : 'text-default-500'}>{s.label}</span>
                  {!s.done && s.hint && <span className="block text-xs text-default-400">{s.hint}</span>}
                </span>
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>

      {/* ── Dueño y equipo ── */}
      <DashboardSection title="Dueño y equipo" subtitle="Quién entra en la consola de la agencia." accent="#06b6d4">
        <div className="grid gap-x-10 gap-y-4 xl:grid-cols-2">
          <div>
            <p className="mb-1 text-xs uppercase tracking-wide text-default-500">Dueño</p>
            {owners == null ? (
              <Spinner size="sm" />
            ) : owners.length === 0 ? (
              <p className="text-sm text-warning-600">No tiene dueño.</p>
            ) : (
              owners.map((o) => (
                <Fact key={o.uid} label={o.uid === agency.ownerId ? 'Principal' : 'Dueño'}>
                  {o.missing ? <span className="text-danger-500">Cuenta borrada ({isViewer ? '********' : o.uid})</span> : (
                    <Link to={`/users/${encodeURIComponent(o.uid)}`} className="text-primary hover:underline">{ownerEmail(o.email) || o.uid}</Link>
                  )}
                  {o.tiktokUsername && !isViewer && <span className="text-default-500"> · @{o.tiktokUsername}</span>}
                  {!o.hasRole && !o.missing && <span className="text-warning-600"> · sin rol (no entra)</span>}
                </Fact>
              ))
            )}
          </div>
          <div>
            <p className="mb-1 text-xs uppercase tracking-wide text-default-500">Equipo ({staff.filter((s) => s.active).length} activos)</p>
            {staff.length === 0 ? (
              <p className="text-sm text-default-500">Sin equipo en Licencias.</p>
            ) : (
              staff.map((s) => (
                <Fact key={s.uid} label={s.role}>
                  <span className={s.active ? '' : 'text-default-400'}>{ownerEmail(s.email) || s.name || s.uid}</span>
                  {!s.active && <span className="text-default-400"> · inactivo</span>}
                  {s.devTest && <span className="text-default-400"> · prueba</span>}
                </Fact>
              ))
            )}
          </div>
        </div>
      </DashboardSection>

      {/* ── Detalle ── */}
      <DashboardSection title="Detalle" accent="#a1a1aa">
        <Tabs size="sm" selectedKey={tab} onSelectionChange={(k) => setTab(String(k))} aria-label="Detalle de la agencia">
          <Tab key="creators" title={`Creadores${countLabel(roster)}`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-default-500">
                {roster == null ? 'Cargando creadores…' : (
                  <>
                    <span className="text-default-700">{roster.length} vigentes</span>
                    {' · '}{rosterStats.licensed} con licencia
                    {' · '}{rosterStats.withApp} con cuenta de TikControl
                    {rosterStats.live > 0 && <span className="text-danger-500">{' · '}{rosterStats.live} en directo</span>}
                    {members && members.length !== roster.length && ` · ${members.length} fichas en total`}
                  </>
                )}
              </p>
              <Button size="sm" variant="flat" onPress={() => setMembersOpen((v) => !v)} isDisabled={members == null}>
                {membersOpen ? 'Ocultar la lista' : 'Ver la lista'}
              </Button>
            </div>
            {membersOpen && (
              <>
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  <Input
                    size="sm"
                    placeholder="Buscar por @ o agente…"
                    value={memberSearch}
                    onValueChange={(v) => { setMemberSearch(v); setMemberPage(1); }}
                    isClearable
                    className="max-w-xs"
                  />
                  <Tabs size="sm" selectedKey={memberFilter} onSelectionChange={(k) => { setMemberFilter(String(k)); setMemberPage(1); }} aria-label="Qué creadores">
                    <Tab key="roster" title={`Vigentes (${roster ? roster.length : '…'})`} />
                    <Tab key="all" title={`Todas las fichas (${members ? members.length : '…'})`} />
                  </Tabs>
                </div>
                <Table aria-label="Creadores" removeWrapper isStriped classNames={{ th: 'bg-default-100' }}>
                  <TableHeader>
                    <TableColumn>Creador</TableColumn>
                    <TableColumn>Estado</TableColumn>
                    <TableColumn>Licencia</TableColumn>
                    <TableColumn>Agente</TableColumn>
                    <TableColumn>Último directo</TableColumn>
                    <TableColumn>TikControl</TableColumn>
                  </TableHeader>
                  <TableBody emptyContent={memberSearch ? 'Ningún creador coincide.' : 'Sin creadores.'}>
                    {pageMembers.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MemberAvatar key={m.avatarUrl || m.id} url={m.avatarUrl} name={m.tiktokUsername} />
                            <span>@{m.tiktokUsername || '—'}</span>
                            {m.backstageIsLive && <Chip size="sm" color="danger" variant="flat">LIVE</Chip>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{MEMBER_STATUS[m.status] || m.status || '—'}</span>
                          {m.inRoster === false && <span className="block text-xs text-default-400">Fuera del roster</span>}
                          {m.accountMissing && <span className="block text-xs text-default-400">Cuenta no disponible</span>}
                        </TableCell>
                        <TableCell>{m.licenseType && m.licenseType !== 'none' ? <Chip size="sm" variant="flat" color="primary">{S.LICENSE_TYPES[m.licenseType]?.label || m.licenseType}</Chip> : '—'}</TableCell>
                        <TableCell>{m.backstageAgent || '—'}</TableCell>
                        <TableCell>{fmtDate(toMs(m.backstageLastLiveAt))}</TableCell>
                        <TableCell>{m.userId ? 'Sí' : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {shownMembers.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs text-default-500">
                      Del {(page - 1) * MEMBERS_PAGE + 1} al {Math.min(page * MEMBERS_PAGE, shownMembers.length)} de {shownMembers.length}
                    </span>
                    {memberPages > 1 && (
                      <Pagination size="sm" showControls total={memberPages} page={page} onChange={setMemberPage} />
                    )}
                  </div>
                )}
              </>
            )}
          </Tab>

          <Tab key="missions" title={`Misiones${countLabel(challenges)}`}>
            <Table aria-label="Misiones" removeWrapper isStriped classNames={{ th: 'bg-default-100' }}>
              <TableHeader>
                <TableColumn>Misión</TableColumn>
                <TableColumn>Tipo</TableColumn>
                <TableColumn>Estado</TableColumn>
                <TableColumn>Desde</TableColumn>
                <TableColumn>Hasta</TableColumn>
              </TableHeader>
              <TableBody emptyContent={challenges == null ? 'Cargando…' : 'Sin misiones.'}>
                {(challenges || [])
                  .map((c) => ({ ...c, _start: toMs(c.startDate) }))
                  .sort((a, b) => b._start - a._start)
                  .slice(0, 300)
                  .map((c) => {
                    const st = S.missionStatus ? S.missionStatus(c) : (c.active === false ? 'paused' : 'active');
                    const [label, color] = MISSION_STATUS[st] || [st, 'default'];
                    return (
                      <TableRow key={c.id}>
                        <TableCell>{c.name || c.title || c.id}</TableCell>
                        <TableCell>{c.autoPlanId || c.autoKind || c.autoKinds ? 'Automática' : 'Específica'}</TableCell>
                        <TableCell><Chip size="sm" variant="flat" color={color}>{label}</Chip></TableCell>
                        <TableCell>{fmtDate(c._start)}</TableCell>
                        <TableCell>{c.deadlineMode === 'relative' ? `${c.relativeDays || 7} días desde que se une` : fmtDate(toMs(c.endDate))}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Tab>

          <Tab key="events" title={`Eventos${countLabel(events)}`}>
            <Table aria-label="Eventos" removeWrapper isStriped classNames={{ th: 'bg-default-100' }}>
              <TableHeader>
                <TableColumn>Evento</TableColumn>
                <TableColumn>Estado</TableColumn>
                <TableColumn>Desde</TableColumn>
                <TableColumn>Hasta</TableColumn>
              </TableHeader>
              <TableBody emptyContent={events == null ? 'Cargando…' : 'Sin eventos.'}>
                {(events || [])
                  .map((e) => ({ ...e, _start: toMs(e.startDate), _end: toMs(e.endDate) }))
                  .sort((a, b) => b._start - a._start)
                  .slice(0, 300)
                  .map((e) => {
                    const now = Date.now();
                    const state = e.draft ? ['Borrador', 'default']
                      : e._end && e._end < now ? ['Terminado', 'default']
                        : e._start && e._start > now ? ['Programado', 'primary']
                          : e.active === false ? ['Desactivado', 'warning'] : ['En curso', 'success'];
                    return (
                      <TableRow key={e.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {e.imageUrl && <img src={e.imageUrl} alt="" className="h-7 w-12 rounded object-cover" />}
                            <span>{e.name || e.title || e.id}</span>
                            {e.mirrorOf && <Chip size="sm" variant="flat">Copia</Chip>}
                          </div>
                        </TableCell>
                        <TableCell><Chip size="sm" variant="flat" color={state[1]}>{state[0]}</Chip></TableCell>
                        <TableCell>{fmtDate(e._start)}</TableCell>
                        <TableCell>{fmtDate(e._end)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Tab>

          <Tab key="activity" title="Historial">
            <Table aria-label="Historial" removeWrapper isStriped classNames={{ th: 'bg-default-100' }}>
              <TableHeader>
                <TableColumn>Fecha</TableColumn>
                <TableColumn>Quién</TableColumn>
                <TableColumn>Acción</TableColumn>
                <TableColumn>Detalle</TableColumn>
              </TableHeader>
              <TableBody emptyContent={activity == null ? 'Cargando…' : 'Sin cambios registrados.'}>
                {sortedActivity.slice(0, 200).map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="whitespace-nowrap">{fmtDateTime(a.atMs)}</TableCell>
                    <TableCell>{isViewer ? maskEmail(a.userName) : a.userName || '—'}</TableCell>
                    <TableCell>
                      {a.actionKey ? S.t(a.actionKey, a.action) : a.action || '—'}
                      {(a.moduleKey || a.module) && <span className="block text-xs text-default-400">{a.moduleKey ? S.t(a.moduleKey, a.module) : a.module}</span>}
                    </TableCell>
                    <TableCell className="text-default-500">{a.detail || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Tab>

          <Tab key="public" title="Ficha pública">
            <p className="mb-3 text-xs text-default-500">
              Así la ven los creadores al abrir «Ver agencia» en el Explorador (con su plan de ahora). La edita su dueño en «Editar vista pública».
            </p>
            <AgencySheet agency={agency} creators={creatorsCount} />
          </Tab>
        </Tabs>
      </DashboardSection>

      <AgencyPlanEditor
        isOpen={planEditor.isOpen}
        onClose={planEditor.onClose}
        agency={agency}
        usage={usage}
        usageReady={members != null && profiles != null && challenges != null && events != null}
        onSaved={(msg) => notify(msg)}
      />
      <AgencyProfileEditor
        isOpen={profileEditor.isOpen}
        onClose={profileEditor.onClose}
        agency={agency}
        creators={creatorsCount}
        onSaved={(msg) => notify(msg)}
      />
      <AgencyOwnerEditor
        isOpen={ownerEditor.isOpen}
        onClose={ownerEditor.onClose}
        agency={agency}
        owners={owners || []}
        onSaved={(msg) => { notify(msg); setOwnerRefresh((n) => n + 1); }}
      />
      <Modal isOpen={jsonView.isOpen} onClose={jsonView.onClose} size="3xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader className="flex flex-col">
            <span>{agency.name || agency.id}</span>
            <span className="text-xs font-normal text-default-400">agencies/{agency.id}</span>
          </ModalHeader>
          <ModalBody>
            <pre className="max-h-[65vh] overflow-auto rounded bg-default-100 p-3 text-xs">{JSON.stringify(agency, null, 2)}</pre>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={jsonView.onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
